import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Play,
  Check,
  Lock,
  Upload,
  MessageSquare,
  Sparkles,
  FileText,
  BarChart3,
  Database,
  ChevronDown,
  Twitter,
  Github,
  Linkedin,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import { DuckMark, DuckPilotWordmark } from '../components/DuckPilotLogo';

// Calm fade-up — the brief is editorial and warm, not flashy.
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

/* ── Editorial section marker: mono kicker with a filed § number ─────── */
const Kicker = ({ index, children, className = '' }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <span className="font-mono text-[11px] font-bold tabular-nums tracking-[0.05em]">{index}</span>
    <span className="h-px w-8 bg-current opacity-30" />
    <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em]">{children}</span>
  </div>
);

/* ── Brand chart: Region growth, Q1 → Q2 (brief chart style) ───────────
   White plot surface · sand-200 horizontal gridlines · sand-400 axis ·
   top-rounded bars · Q1 terracotta, Q2 sage · Space Grotesk tabular labels. */
const RegionChart = ({ height = 190 }) => (
  <svg width="100%" height={height} viewBox="0 0 460 190" role="img" aria-label="Revenue by region chart">
    <g stroke="#eee7db" strokeWidth="1">
      <line x1="40" y1="20" x2="450" y2="20" />
      <line x1="40" y1="60" x2="450" y2="60" />
      <line x1="40" y1="100" x2="450" y2="100" />
      <line x1="40" y1="140" x2="450" y2="140" />
    </g>
    <line x1="40" y1="164" x2="450" y2="164" stroke="#c0b6a5" strokeWidth="1.5" />
    <g fontFamily="Space Grotesk" fontSize="10" fill="#82796a" textAnchor="end">
      <text x="34" y="24">80k</text>
      <text x="34" y="64">60k</text>
      <text x="34" y="104">40k</text>
      <text x="34" y="144">20k</text>
    </g>
    <g>
      <rect x="70" y="52" width="30" height="112" rx="6" fill="#c67139" />
      <rect x="104" y="84" width="30" height="80" rx="6" fill="#7a8a5e" />
      <rect x="180" y="36" width="30" height="128" rx="6" fill="#c67139" />
      <rect x="214" y="72" width="30" height="92" rx="6" fill="#7a8a5e" />
      <rect x="290" y="94" width="30" height="70" rx="6" fill="#c67139" />
      <rect x="324" y="108" width="30" height="56" rx="6" fill="#7a8a5e" />
      <rect x="380" y="118" width="30" height="46" rx="6" fill="#c67139" />
      <rect x="414" y="130" width="30" height="34" rx="6" fill="#7a8a5e" />
    </g>
    <g fontFamily="Space Grotesk" fontSize="10.5" fill="#645c50" textAnchor="middle">
      <text x="102" y="181">West</text>
      <text x="212" y="181">East</text>
      <text x="322" y="181">South</text>
      <text x="412" y="181">North</text>
    </g>
  </svg>
);

/* Trust tick — mono, ledger-style */
const TrustTick = ({ children }) => (
  <span className="inline-flex items-center gap-2 font-mono text-[11.5px] uppercase tracking-[0.06em] text-sand-700">
    <Check className="h-3.5 w-3.5 text-sage-600" strokeWidth={3} />
    {children}
  </span>
);

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const faqs = [
    {
      question: 'How does DuckPilot keep my data private?',
      answer:
        'Everything runs locally in your browser on DuckDB (WebAssembly). Your rows never leave the machine. The AI sees only your column names and the small result already on your screen — never the file.',
    },
    {
      question: 'What file formats are supported?',
      answer: 'CSV, Excel (.xlsx, .xls), JSON, and Parquet. Drop a file and it opens right here.',
    },
    {
      question: 'Can I use DuckPilot offline?',
      answer: 'Yes — once loaded, querying works entirely offline. AI-written summaries need connectivity.',
    },
    {
      question: 'Is there a limit on file size?',
      answer: "Free analyzes files up to 50MB. Pro is constrained only by your browser's memory.",
    },
  ];
  return (
    <div className="border-t border-[rgba(32,30,29,0.14)]">
      {faqs.map((faq, i) => (
        <div key={i} className="border-b border-[rgba(32,30,29,0.14)]">
          <button
            className="group w-full py-6 flex items-start gap-5 text-left transition-colors hover:text-bill-700"
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
          >
            <span className="font-mono text-[12px] font-bold tabular-nums text-sand-500 pt-1.5 group-hover:text-bill-600 transition-colors">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="flex-1 font-display text-[21px] md:text-[23px] leading-snug text-ink group-hover:text-bill-700 transition-colors">
              {faq.question}
            </span>
            <ChevronDown
              className={`w-5 h-5 text-sand-500 mt-1.5 shrink-0 transition-transform ${openIndex === i ? 'rotate-180 text-bill-600' : ''}`}
            />
          </button>
          <AnimatePresence initial={false}>
            {openIndex === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="pb-7 pl-10 pr-8 text-[15px] text-sand-700 leading-relaxed max-w-2xl">{faq.answer}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setAuthMode('signup');
      setShowAuthModal(true);
    }
  };

  const handleSignIn = () => {
    setAuthMode('signin');
    setShowAuthModal(true);
  };

  const handleTryDemo = () => navigate('/dashboard?mode=demo');

  const steps = [
    {
      title: 'Drop your file',
      description: 'CSV, Excel, JSON, or Parquet. It opens right here in the browser — nothing uploads.',
      icon: Upload,
    },
    {
      title: 'Ask in plain English',
      description: 'Type a question. DuckPilot writes the SQL and runs it locally in milliseconds.',
      icon: MessageSquare,
    },
    {
      title: 'Get table, chart & summary',
      description: 'A clear answer you can pin to a branded report and export as a PDF.',
      icon: Sparkles,
    },
  ];

  const features = [
    {
      icon: MessageSquare,
      title: 'Ask in plain English',
      body: 'Natural language to SQL, without the learning curve. Just type what you want to know.',
      tag: 'NL → SQL',
    },
    {
      icon: Lock,
      title: 'Private by default',
      body: 'Your rows never leave the browser. The AI only ever sees column names and on-screen results.',
      tag: '0 ROWS',
    },
    {
      icon: BarChart3,
      title: 'Answers with charts',
      body: 'Every result comes with a clean, readable chart — not a wall of numbers.',
      tag: 'AUTO-VIZ',
    },
    {
      icon: FileText,
      title: 'Branded PDF reports',
      body: 'Pin answers, add a client logo, export a Letter-size report. Built locally.',
      tag: 'PDF · LETTER',
    },
    {
      icon: Database,
      title: 'Powered by DuckDB',
      body: 'Blazing OLAP queries running entirely in your browser via WebAssembly.',
      tag: 'WASM · OLAP',
    },
    {
      icon: Sparkles,
      title: 'AI insights',
      body: 'Plain-English summaries written from the rows already on your screen.',
      tag: 'ON-DEVICE CTX',
    },
  ];

  const tickerItems = [
    'DUCKDB · WASM',
    '0 ROWS UPLOADED',
    'CSV',
    'XLSX',
    'JSON',
    'PARQUET',
    'NL → SQL',
    'RUNS OFFLINE',
    'BRANDED PDF',
    'ON YOUR DESK',
  ];

  return (
    <div className="min-h-screen bg-paper text-ink font-body relative overflow-x-hidden">
      {/* Notebook dot-grid — faint ledger texture over the whole page */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(rgba(32,30,29,0.045) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
        }}
      />
      {/* Warm edge wash — subtle, corners only */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-48 -right-48 w-[640px] h-[640px] rounded-full opacity-70"
          style={{ background: 'radial-gradient(circle, rgba(198,113,57,0.10) 0%, transparent 70%)', filter: 'blur(40px)' }}
        />
        <div
          className="absolute top-[52%] -left-48 w-[520px] h-[520px] rounded-full opacity-70"
          style={{ background: 'radial-gradient(circle, rgba(122,138,94,0.10) 0%, transparent 70%)', filter: 'blur(40px)' }}
        />
      </div>

      {/* Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-paper/85 backdrop-blur-xl border-b border-[rgba(32,30,29,0.12)]' : 'border-b border-transparent'
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6">
          <button type="button" onClick={() => scrollToSection('hero')} className="flex items-center">
            <DuckPilotWordmark size={30} fontSize={21} />
          </button>
          <nav className="hidden items-center gap-9 font-mono text-[11.5px] uppercase tracking-[0.14em] text-sand-700 md:flex">
            <button type="button" onClick={() => scrollToSection('features')} className="hover:text-bill-700 transition-colors">Features</button>
            <button type="button" onClick={() => scrollToSection('how-it-works')} className="hover:text-bill-700 transition-colors">How&nbsp;it&nbsp;works</button>
            <button type="button" onClick={() => scrollToSection('pricing')} className="hover:text-bill-700 transition-colors">Pricing</button>
          </nav>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="rounded-full bg-bill-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-bill-600 transition-colors shadow-brand-sm"
              >
                Open app
              </button>
            ) : (
              <>
                <button type="button" onClick={handleSignIn} className="hidden sm:block font-mono text-[11.5px] uppercase tracking-[0.14em] text-sand-700 hover:text-ink transition-colors">
                  Sign in
                </button>
                <button
                  type="button"
                  onClick={handleGetStarted}
                  className="rounded-full bg-bill-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-bill-600 transition-colors shadow-brand-sm"
                >
                  Get started free
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section id="hero" className="relative">
        <div className="mx-auto max-w-7xl px-6 pt-14 pb-20 lg:pt-20">
          <div className="grid items-center gap-16 lg:grid-cols-[1.05fr_0.95fr]">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="space-y-7">
              {/* Filing tab / release marker */}
              <button
                type="button"
                onClick={() => scrollToSection('features')}
                className="group inline-flex items-center gap-2.5 border border-[rgba(32,30,29,0.18)] bg-white/70 py-1.5 pl-1.5 pr-3.5 backdrop-blur transition-all hover:border-bill-400"
                style={{ borderRadius: 4 }}
              >
                <span className="inline-flex items-center gap-1 bg-sage-500 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-white" style={{ borderRadius: 2 }}>
                  New
                </span>
                <span className="inline-flex items-center gap-1.5 font-mono text-[11.5px] uppercase tracking-[0.1em] text-ink">
                  DuckDB&nbsp;1.2 <span className="text-sand-400">·</span> AI&nbsp;Insights
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-sand-500 transition-transform group-hover:translate-x-0.5" />
              </button>

              <Kicker index="§00" className="text-bill-700">Local-first analytics desk</Kicker>

              <h1 className="font-display text-[46px] leading-[1.0] tracking-[-0.01em] md:text-6xl lg:text-[70px]">
                Turn CSVs into <span className="relative inline-block text-bill-500">
                  insights
                  <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 200 10" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M2 7 Q 50 2 100 6 T 198 5" fill="none" stroke="#c67139" strokeWidth="3" strokeLinecap="round" opacity="0.9" />
                  </svg>
                </span>, without the cloud.
              </h1>

              <p className="max-w-xl text-[17px] leading-relaxed text-sand-700">
                DuckPilot is a privacy-first analytics desk. Drop your data, ask questions in plain English, and get
                answers with tables and charts — everything runs locally in your browser.
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleGetStarted}
                  className="group inline-flex items-center gap-2 rounded-full bg-bill-500 px-7 py-3.5 text-[15px] font-semibold text-white hover:bg-bill-600 transition-all shadow-brand-md"
                >
                  Start for free
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button
                  type="button"
                  onClick={handleTryDemo}
                  className="inline-flex items-center gap-2 rounded-full border border-[rgba(32,30,29,0.2)] bg-white px-7 py-3.5 text-[15px] font-semibold text-ink hover:bg-sand-100 transition-all"
                >
                  <Play className="h-4 w-4 text-bill-500" />
                  Try live demo
                </button>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2.5 pt-1">
                <TrustTick>No signup</TrustTick>
                <TrustTick>No API key</TrustTick>
                <TrustTick>Rows never leave the browser</TrustTick>
              </div>
            </motion.div>

            {/* On-brand product mock — a filed report, stacked on the desk */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              {/* under-sheet, gives a "stack of reports" depth */}
              <div className="absolute inset-0 translate-x-3 translate-y-4 rotate-[2.5deg] rounded-brand-lg border border-[rgba(32,30,29,0.1)] bg-sand-100" />

              {/* filed corner stamp */}
              <div
                className="absolute -top-3 right-6 z-20 rotate-[8deg] border-2 border-sage-600/70 bg-sage-100/90 px-3 py-1 font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] text-sage-700 shadow-brand-sm"
                style={{ borderRadius: 4 }}
              >
                Filed · local
              </div>

              <div className="relative rounded-brand-lg border border-[rgba(32,30,29,0.14)] bg-white shadow-brand-lg overflow-hidden">
                {/* window chrome */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-[rgba(32,30,29,0.08)] bg-sand-100">
                  <span className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-sand-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-sand-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-sand-300" />
                  </span>
                  <span className="flex-1 mx-3">
                    <span className="flex h-6 items-center rounded-full bg-white border border-[rgba(32,30,29,0.08)] px-3 font-mono text-[11px] text-sand-600">
                      duckpilot · client-sales.csv
                    </span>
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-display text-[19px]">Region growth, Q1 → Q2 2026</div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-sage-200 px-3 py-1 font-mono text-[10.5px] font-semibold text-sage-800 tnum">
                      <Lock className="w-3 h-3" strokeWidth={2.75} />
                      412 B → AI
                    </span>
                  </div>
                  <RegionChart height={200} />
                  <div className="flex gap-4 mt-2 font-mono text-[11px] uppercase tracking-[0.06em] text-sand-700">
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] bg-bill-500" />Q1</span>
                    <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px] bg-sage-500" />Q2</span>
                  </div>
                  <div className="mt-4 rounded-brand-md bg-sage-100 p-4 flex gap-3">
                    <DuckMark size={18} variant="sage" style={{ flex: 'none', marginTop: 2 }} />
                    <p className="m-0 text-[13px] leading-relaxed text-sage-900">
                      <strong>East grew fastest</strong>, up 28.9% quarter over quarter — driven by order volume rather
                      than price. North is the only region that shrank.
                    </p>
                  </div>
                </div>
              </div>

              <div className="absolute -left-4 top-10 hidden sm:flex items-center gap-2 rounded-full bg-white border border-[rgba(32,30,29,0.14)] px-3.5 py-2 shadow-brand-md">
                <Database className="w-4 h-4 text-bill-500" />
                <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink">DuckDB WASM</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Data ticker — a mono marquee of what runs where */}
      <div className="relative overflow-hidden border-y border-[rgba(32,30,29,0.16)] bg-ink py-3.5">
        <motion.div
          className="flex w-max whitespace-nowrap"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ repeat: Infinity, duration: 28, ease: 'linear' }}
        >
          {[...tickerItems, ...tickerItems].map((t, i) => (
            <span key={i} className="flex items-center gap-6 pr-6 font-mono text-[12px] uppercase tracking-[0.2em] text-paper/75">
              {t}
              <span className="text-bill-400">◆</span>
            </span>
          ))}
        </motion.div>
      </div>

      {/* Zero-setup drop zone — a torn ledger sheet */}
      <section className="px-6 py-24">
        <motion.div {...fadeUp} className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-[28px] border-2 border-dashed border-sand-400 bg-white/70 px-8 py-14 flex flex-col items-center gap-5 text-center backdrop-blur-sm">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-full opacity-[0.5]"
              style={{
                backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0 33px, rgba(32,30,29,0.05) 33px 34px)',
              }}
            />
            <div className="relative flex flex-col items-center gap-5">
              <DuckMark size={60} />
              <h2 className="font-display text-3xl md:text-[34px] m-0">Drop a CSV. Ask it anything.</h2>
              <p className="m-0 max-w-md text-[15px] text-sand-700">
                Your file opens right here in the browser. Plain-English questions in, answers with tables and charts out.
              </p>
              <button
                type="button"
                onClick={handleTryDemo}
                className="rounded-full bg-bill-500 px-6 py-3 text-sm font-semibold text-white hover:bg-bill-600 transition-colors shadow-brand-md"
              >
                Choose a file
              </button>
              <span className="font-mono text-[11.5px] uppercase tracking-[0.08em] text-sand-600">
                or try a{' '}
                <button type="button" onClick={handleTryDemo} className="font-semibold text-bill-700 hover:text-bill-800 underline decoration-dotted underline-offset-4">
                  sample dataset
                </button>
              </span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Features — a printed capability index */}
      <section id="features" className="px-6 pb-24">
        <div className="mx-auto max-w-5xl">
          <motion.div {...fadeUp} className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <Kicker index="§01" className="text-bill-700 mb-4">Features</Kicker>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">Everything you need to own your data</h2>
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-sand-500">Capability index · 06 entries</span>
          </motion.div>

          <motion.div {...fadeUp} className="overflow-hidden rounded-brand-lg border border-[rgba(32,30,29,0.14)] bg-white shadow-brand-sm">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="group relative grid grid-cols-[auto_1fr] items-start gap-5 border-t border-[rgba(32,30,29,0.12)] px-6 py-7 transition-colors first:border-t-0 hover:bg-sand-100/70 md:grid-cols-[auto_1fr_auto] md:gap-8 md:px-8"
              >
                <span className="absolute left-0 top-0 h-full w-[3px] origin-top scale-y-0 bg-bill-500 transition-transform duration-300 group-hover:scale-y-100" />
                <span className="font-mono text-[13px] font-bold tabular-nums text-sand-400 pt-1.5 group-hover:text-bill-600 transition-colors">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <div className="flex items-center gap-2.5">
                    <f.icon className="h-5 w-5 text-bill-600" strokeWidth={2.25} />
                    <h3 className="font-display text-[22px] leading-tight text-ink">{f.title}</h3>
                  </div>
                  <p className="mt-1.5 max-w-lg text-[14.5px] leading-relaxed text-sand-700">{f.body}</p>
                </div>
                <span className="hidden shrink-0 self-center border border-[rgba(32,30,29,0.14)] bg-sand-100 px-2.5 py-1 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-sand-700 md:block" style={{ borderRadius: 4 }}>
                  {f.tag}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Privacy — a transmission receipt */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-6xl grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fadeUp}>
            <Kicker index="§02" className="text-sage-700 mb-4">Privacy, made visible</Kicker>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mb-4">You can see exactly what left the browser.</h2>
            <p className="text-[16px] text-sand-700 leading-relaxed mb-6">
              Privacy isn't a footnote here — it's a surface you can open. Every answer shows precisely what the AI
              received: your column names, your question, and the handful of result rows already on your screen. Your raw
              file is never part of the conversation.
            </p>
            <span className="inline-flex items-center gap-2 border border-[rgba(32,30,29,0.16)] bg-white px-3.5 py-2 font-mono text-[11.5px] uppercase tracking-[0.06em] text-sand-700 shadow-brand-sm tnum" style={{ borderRadius: 4 }}>
              <Lock className="w-3.5 h-3.5 text-sage-600" strokeWidth={2.75} /> On this device · 0 raw rows, ever
            </span>
          </motion.div>

          <motion.div {...fadeUp} className="relative">
            {/* rubber stamp */}
            <div
              className="absolute -top-4 -right-2 z-10 rotate-[10deg] border-[2.5px] border-bill-500/70 bg-bill-100/80 px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-bill-700 shadow-brand-sm"
              style={{ borderRadius: 5 }}
            >
              Payload 412 B
            </div>

            {/* receipt */}
            <div className="rounded-brand-md bg-white border border-[rgba(32,30,29,0.16)] shadow-brand-md overflow-hidden">
              <div className="flex items-center justify-between border-b border-dashed border-sand-300 bg-sand-100 px-5 py-3">
                <span className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-ink">
                  <DuckMark size={15} variant="mono" /> Transmission receipt
                </span>
                <span className="font-mono text-[10.5px] tabular-nums text-sand-600">2026-07 · LOCAL</span>
              </div>

              <div className="px-5 py-5 font-mono text-[12px]">
                <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-sage-700 mb-2.5">Sent to AI</div>
                {['order_id · int', 'order_date · date', 'region · text', 'revenue · decimal', '+ 3 columns'].map((c) => (
                  <div key={c} className="flex items-baseline gap-2 py-[3px]">
                    <span className="text-sand-800">{c}</span>
                    <span className="flex-1 self-center border-b border-dotted border-sand-400" />
                    <span className="font-semibold text-sage-700">SENT</span>
                  </div>
                ))}

                <div className="my-4 border-t border-dashed border-sand-300" />

                <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-bill-700 mb-2.5">Never sent</div>
                {['Your raw rows', 'The CSV file itself', 'Any cell values', 'Anything after you close the tab'].map((c) => (
                  <div key={c} className="flex items-baseline gap-2 py-[3px]">
                    <span className="text-sand-700">{c}</span>
                    <span className="flex-1 self-center border-b border-dotted border-sand-300" />
                    <span className="font-bold text-bill-600">✕</span>
                  </div>
                ))}

                <div className="mt-4 flex items-center justify-between border-t-2 border-ink/80 pt-3 text-[12.5px] font-bold text-ink">
                  <span>PAYLOAD TOTAL</span>
                  <span className="tabular-nums">412 BYTES</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works — a filmstrip with an oversized numeral */}
      <section id="how-it-works" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <motion.div {...fadeUp} className="mb-14 flex flex-col items-center gap-4 text-center">
            <Kicker index="§03" className="text-bill-700">How it works</Kicker>
            <h2 className="font-display text-4xl md:text-5xl">Three steps to data clarity</h2>
          </motion.div>
          <div className="relative grid gap-6 lg:grid-cols-3">
            {/* dashed connecting rail */}
            <div className="pointer-events-none absolute top-[52px] left-[16%] right-[16%] hidden border-t-2 border-dashed border-sand-300 lg:block" />
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.12 }}
                className="relative overflow-hidden rounded-brand-lg border border-[rgba(32,30,29,0.14)] bg-white p-8 shadow-brand-sm"
              >
                {/* watermark numeral */}
                <span className="pointer-events-none absolute -top-4 right-2 font-display text-[96px] leading-none text-sand-200 select-none">
                  {index + 1}
                </span>
                <div className="relative">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-brand-sm bg-bill-100 text-bill-700 ring-4 ring-paper">
                    <step.icon className="h-6 w-6" strokeWidth={2.25} />
                  </div>
                  <div className="mb-2 font-mono text-[10.5px] font-bold uppercase tracking-[0.16em] text-sand-500">
                    Step {String(index + 1).padStart(2, '0')}
                  </div>
                  <h3 className="mb-2 font-display text-[24px] leading-tight text-ink">{step.title}</h3>
                  <p className="text-[14.5px] leading-relaxed text-sand-700">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing — two invoices */}
      <section id="pricing" className="px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <motion.div {...fadeUp} className="mb-12 flex flex-col items-center gap-4 text-center">
            <Kicker index="§04" className="text-bill-700">Pricing</Kicker>
            <h2 className="font-display text-4xl md:text-5xl">Start free. Upgrade when it earns its keep.</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-5">
            {/* Free */}
            <motion.div {...fadeUp} className="rounded-brand-lg border border-[rgba(32,30,29,0.14)] bg-white p-8 shadow-brand-sm">
              <div className="flex items-baseline justify-between border-b border-dashed border-sand-300 pb-4">
                <span className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-sand-600">Plan / Free</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-sand-500">Forever</span>
              </div>
              <div className="mt-5 mb-1 font-display text-5xl">$0</div>
              <div className="mb-6 text-[13px] text-sand-600">Everything local, forever.</div>
              <ul className="mb-8 space-y-3 font-mono text-[12.5px] text-sand-800">
                {['Files up to 50 MB', 'Plain-English questions', 'Charts + AI summaries', 'Runs fully offline'].map((x) => (
                  <li key={x} className="flex items-baseline gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage-600" strokeWidth={3} />
                    <span>{x}</span>
                    <span className="flex-1 self-center border-b border-dotted border-sand-300" />
                    <span className="text-sage-700 font-semibold">INCL</span>
                  </li>
                ))}
              </ul>
              <button onClick={handleGetStarted} className="w-full rounded-full border border-[rgba(32,30,29,0.2)] bg-white py-3 text-sm font-semibold text-ink hover:bg-sand-100 transition-colors">
                Get started free
              </button>
            </motion.div>

            {/* Pro */}
            <motion.div {...fadeUp} className="relative overflow-hidden rounded-brand-lg border-2 border-bill-500 bg-white p-8 shadow-brand-md">
              {/* corner fold */}
              <div className="absolute right-0 top-0 h-0 w-0 border-l-[54px] border-b-[54px] border-l-transparent border-b-transparent border-t-[54px] border-t-bill-500" />
              <span className="absolute right-1.5 top-2 rotate-45 font-mono text-[9px] font-bold uppercase tracking-[0.08em] text-white">Popular</span>
              <div className="flex items-baseline justify-between border-b border-dashed border-sand-300 pb-4">
                <span className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-bill-700">Plan / Pro</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-sand-500">Monthly</span>
              </div>
              <div className="mt-5 mb-1 font-display text-5xl">$19<span className="font-body text-lg text-sand-600">/mo</span></div>
              <div className="mb-6 text-[13px] text-sand-600">For consultants shipping client work.</div>
              <ul className="mb-8 space-y-3 font-mono text-[12.5px] text-sand-800">
                {['Unlimited file size', 'Branded PDF report builder', 'Priority AI models', 'Everything in Free'].map((x) => (
                  <li key={x} className="flex items-baseline gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-bill-600" strokeWidth={3} />
                    <span>{x}</span>
                    <span className="flex-1 self-center border-b border-dotted border-sand-300" />
                    <span className="text-bill-700 font-semibold">PRO</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/pricing')} className="w-full rounded-full bg-bill-500 py-3 text-sm font-semibold text-white hover:bg-bill-600 transition-colors shadow-brand-sm">
                See full pricing
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ — ledger rows */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <motion.div {...fadeUp} className="mb-10 flex flex-col gap-4">
            <Kicker index="§05" className="text-bill-700">Questions</Kicker>
            <h2 className="font-display text-4xl md:text-5xl">Frequently asked</h2>
          </motion.div>
          <FAQ />
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-24">
        <motion.div
          {...fadeUp}
          className="mx-auto max-w-5xl rounded-[36px] bg-ink px-8 py-20 text-center relative overflow-hidden"
        >
          {/* ledger grid on ink */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.5]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(245,234,216,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(245,234,216,0.05) 1px, transparent 1px)',
              backgroundSize: '42px 42px',
            }}
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(600px circle at 50% 0%, rgba(198,113,57,0.3), transparent 70%)' }}
          />
          {/* stamp */}
          <div
            className="absolute left-6 top-8 hidden rotate-[-9deg] border-2 border-paper/30 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-paper/50 md:block"
            style={{ borderRadius: 5 }}
          >
            Private by default
          </div>
          <div className="relative">
            <DuckMark size={56} variant="reversed" className="mx-auto mb-6" />
            <h2 className="font-display text-4xl md:text-6xl text-paper mb-5">Ready to own your data?</h2>
            <p className="text-[17px] text-paper/70 mb-9 max-w-2xl mx-auto">
              Analytics that read like your own desk — warm, fast, and private by default.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={handleGetStarted}
                className="group inline-flex items-center gap-2 rounded-full bg-bill-500 px-8 py-4 text-white font-semibold text-[15px] hover:bg-bill-600 transition-all shadow-brand-md"
              >
                Start for free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={handleTryDemo}
                className="inline-flex items-center gap-2 rounded-full border border-paper/25 px-8 py-4 text-paper font-semibold text-[15px] hover:bg-paper/10 transition-colors"
              >
                <Play className="w-4 h-4" />
                Try live demo
              </button>
            </div>
            <div className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 font-mono text-[11.5px] uppercase tracking-[0.08em] text-paper/60 tnum">
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-sage-400" strokeWidth={3} />No credit card</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-sage-400" strokeWidth={3} />Free forever plan</span>
              <span className="flex items-center gap-2"><Check className="w-4 h-4 text-sage-400" strokeWidth={3} />Cancel anytime</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[rgba(32,30,29,0.14)]">
        <div className="mx-auto max-w-7xl px-6 py-14">
          {/* colophon */}
          <div className="mb-10 flex items-center gap-4 font-mono text-[10.5px] uppercase tracking-[0.16em] text-sand-500">
            <span className="h-px flex-1 bg-[rgba(32,30,29,0.12)]" />
            Built locally · Filed on your desk
            <span className="h-px flex-1 bg-[rgba(32,30,29,0.12)]" />
          </div>
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-1">
              <DuckPilotWordmark size={28} fontSize={19} className="mb-3" />
              <p className="text-sm text-sand-700 leading-relaxed">Local-first analytics for teams who care about privacy.</p>
            </div>
            <div>
              <h4 className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-ink">Product</h4>
              <ul className="space-y-3 text-sm text-sand-700">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-bill-700 transition-colors">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-bill-700 transition-colors">Pricing</button></li>
                <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-bill-700 transition-colors">How it works</button></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-ink">Resources</h4>
              <ul className="space-y-3 text-sm text-sand-600">
                <li>Documentation</li>
                <li>API Reference</li>
                <li>Blog</li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-ink">Company</h4>
              <ul className="space-y-3 text-sm text-sand-600">
                <li>About</li>
                <li>Privacy</li>
                <li>Terms</li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-[rgba(32,30,29,0.12)] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-mono text-[11.5px] uppercase tracking-[0.06em] text-sand-600">© 2026 DuckPilot · Built locally, with privacy in mind.</p>
            <div className="flex gap-4 text-sand-500">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:text-ink transition-colors"><Twitter className="w-5 h-5" /></a>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="hover:text-ink transition-colors"><Github className="w-5 h-5" /></a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="hover:text-ink transition-colors"><Linkedin className="w-5 h-5" /></a>
            </div>
          </div>
        </div>
      </footer>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={() => navigate('/dashboard')}
        initialMode={authMode}
      />
    </div>
  );
};

export default LandingPage;
